import React, { useState, useEffect } from 'react';
import { Globe, Smartphone, Monitor, BarChart3, TrendingUp, Target } from 'lucide-react';

const PlatformPlacement = ({ dashboardData, filters, onFilterChange }) => {
  const [selectedMetric, setSelectedMetric] = useState('ctr');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedPlacement, setSelectedPlacement] = useState('all');
  const [viewMode, setViewMode] = useState('platform'); // 'platform', 'placement', 'comparison'

  const platforms = [
    { key: 'meta', label: 'Meta (Facebook/Instagram)', icon: '📘', color: 'blue' },
    { key: 'tiktok', label: 'TikTok', icon: '🎵', color: 'black' },
    { key: 'youtube', label: 'YouTube', icon: '📺', color: 'red' },
    { key: 'pinterest', label: 'Pinterest', icon: '📌', color: 'red' },
    { key: 'google', label: 'Google Ads', icon: '🔍', color: 'blue' },
    { key: 'linkedin', label: 'LinkedIn', icon: '💼', color: 'blue' }
  ];

  const placements = [
    { key: 'feed', label: 'Feed', icon: '📱', category: 'social' },
    { key: 'stories', label: 'Stories', icon: '📖', category: 'social' },
    { key: 'reels', label: 'Reels/Shorts', icon: '🎬', category: 'video' },
    { key: 'in_stream', label: 'In-Stream Video', icon: '🎥', category: 'video' },
    { key: 'search', label: 'Search Results', icon: '🔍', category: 'search' },
    { key: 'display', label: 'Display Network', icon: '🖼️', category: 'display' },
    { key: 'shopping', label: 'Shopping', icon: '🛍️', category: 'commerce' }
  ];

  const metrics = [
    { key: 'ctr', label: 'CTR', icon: '📊', format: 'percentage', color: 'green' },
    { key: 'cvr', label: 'CVR', icon: '📉', format: 'percentage', color: 'purple' },
    { key: 'roas', label: 'ROAS', icon: '🚀', format: 'multiplier', color: 'blue' },
    { key: 'cpa', label: 'CPA', icon: '💸', format: 'currency', color: 'red' },
    { key: 'cpm', label: 'CPM', icon: '📈', format: 'currency', color: 'yellow' }
  ];

  // Mock data for demonstration - in real app this would come from API
  const mockData = {
    platform: {
      meta: { ctr: 0.025, cvr: 0.078, roas: 3.4, cpa: 14.20, cpm: 12.50 },
      tiktok: { ctr: 0.032, cvr: 0.085, roas: 3.8, cpa: 12.80, cpm: 11.20 },
      youtube: { ctr: 0.018, cvr: 0.065, roas: 2.9, cpa: 16.50, cpm: 15.80 },
      pinterest: { ctr: 0.022, cvr: 0.072, roas: 3.1, cpa: 15.20, cpm: 14.20 },
      google: { ctr: 0.020, cvr: 0.068, roas: 3.0, cpa: 15.80, cpm: 16.50 },
      linkedin: { ctr: 0.015, cvr: 0.055, roas: 2.5, cpa: 18.50, cpm: 18.20 }
    },
    placement: {
      feed: { ctr: 0.026, cvr: 0.078, roas: 3.4, cpa: 14.50, cpm: 13.20 },
      stories: { ctr: 0.030, cvr: 0.082, roas: 3.6, cpa: 13.80, cpm: 12.50 },
      reels: { ctr: 0.035, cvr: 0.088, roas: 3.9, cpa: 12.20, cpm: 11.80 },
      in_stream: { ctr: 0.020, cvr: 0.065, roas: 2.8, cpa: 16.80, cpm: 15.50 },
      search: { ctr: 0.018, cvr: 0.062, roas: 2.9, cpa: 17.20, cpm: 18.50 },
      display: { ctr: 0.015, cvr: 0.055, roas: 2.4, cpa: 19.50, cpm: 20.20 },
      shopping: { ctr: 0.022, cvr: 0.070, roas: 3.2, cpa: 15.50, cpm: 14.80 }
    }
  };

  const formatValue = (value, format) => {
    switch (format) {
      case 'percentage':
        return `${(value * 100).toFixed(2)}%`;
      case 'currency':
        return `$${value.toFixed(2)}`;
      case 'multiplier':
        return `${value.toFixed(2)}x`;
      default:
        return value.toFixed(2);
    }
  };

  const getMetricColor = (value, metric, dataType) => {
    const data = mockData[dataType];
    const values = Object.values(data).map(item => item[metric]);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min;
    
    if (range === 0) return 'text-slate-400';
    
    const normalized = (value - min) / range;
    
    if (metric === 'cpa' || metric === 'cpm') {
      // Lower values are better for cost metrics
      return normalized <= 0.33 ? 'text-green-400' : normalized <= 0.66 ? 'text-yellow-400' : 'text-red-400';
    } else {
      // Higher values are better for other metrics
      return normalized >= 0.67 ? 'text-green-400' : normalized >= 0.33 ? 'text-yellow-400' : 'text-red-400';
    }
  };

  const renderPlatformAnalysis = () => {
    const data = mockData.platform;
    const metricData = metrics.find(m => m.key === selectedMetric);
    
    if (!data || !metricData) return <div>No data available</div>;

    const sortedPlatforms = Object.entries(data)
      .map(([key, value]) => ({ key, value: value[selectedMetric] }))
      .sort((a, b) => {
        if (selectedMetric === 'cpa' || selectedMetric === 'cpm') {
          return a.value - b.value; // Lower is better
        } else {
          return b.value - a.value; // Higher is better
        }
      });

    const maxValue = Math.max(...sortedPlatforms.map(d => d.value));
    const minValue = Math.min(...sortedPlatforms.map(d => d.value));

    return (
      <div className="space-y-4">
        {/* Platform Performance Chart */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h5 className="text-lg font-semibold text-white mb-4">Platform Performance by {metricData.label}</h5>
          
          <div className="space-y-3">
            {sortedPlatforms.map((platform) => {
              const platformInfo = platforms.find(p => p.key === platform.key);
              const percentage = maxValue > 0 ? ((platform.value - minValue) / (maxValue - minValue)) * 100 : 0;
              const isBest = selectedMetric === 'cpa' || selectedMetric === 'cpm' ? 
                platform.value === minValue : platform.value === maxValue;
              
              return (
                <div key={platform.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{platformInfo?.icon}</span>
                      <span className="text-sm font-medium text-slate-300">{platformInfo?.label}</span>
                      {isBest && (
                        <span className="px-2 py-1 text-xs bg-green-600/50 text-green-200 rounded-full">
                          Best
                        </span>
                      )}
                    </div>
                    <span className={`text-sm font-semibold ${getMetricColor(platform.value, selectedMetric, 'platform')}`}>
                      {formatValue(platform.value, metricData.format)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        isBest ? 'bg-green-500' : 'bg-purple-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platform Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="text-2xl font-bold text-green-400">
              {platforms.find(p => p.key === sortedPlatforms[0]?.key)?.label}
            </div>
            <div className="text-sm text-slate-400">Top Platform</div>
            <div className="text-xs text-green-300">
              {formatValue(sortedPlatforms[0]?.value || 0, metricData.format)}
            </div>
          </div>
          <div className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="text-2xl font-bold text-blue-400">
              {formatValue(
                sortedPlatforms.reduce((sum, p) => sum + p.value, 0) / sortedPlatforms.length,
                metricData.format
              )}
            </div>
            <div className="text-sm text-slate-400">Average</div>
          </div>
          <div className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="text-2xl font-bold text-red-400">
              {platforms.find(p => p.key === sortedPlatforms[sortedPlatforms.length - 1]?.key)?.label}
            </div>
            <div className="text-sm text-slate-400">Bottom Platform</div>
            <div className="text-xs text-red-300">
              {formatValue(sortedPlatforms[sortedPlatforms.length - 1]?.value || 0, metricData.format)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPlacementAnalysis = () => {
    const data = mockData.placement;
    const metricData = metrics.find(m => m.key === selectedMetric);
    
    if (!data || !metricData) return <div>No data available</div>;

    const sortedPlacements = Object.entries(data)
      .map(([key, value]) => ({ key, value: value[selectedMetric] }))
      .sort((a, b) => {
        if (selectedMetric === 'cpa' || selectedMetric === 'cpm') {
          return a.value - b.value; // Lower is better
        } else {
          return b.value - a.value; // Higher is better
        }
      });

    const maxValue = Math.max(...sortedPlacements.map(d => d.value));
    const minValue = Math.min(...sortedPlacements.map(d => d.value));

    return (
      <div className="space-y-4">
        {/* Placement Performance Chart */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h5 className="text-lg font-semibold text-white mb-4">Placement Performance by {metricData.label}</h5>
          
          <div className="space-y-3">
            {sortedPlacements.map((placement) => {
              const placementInfo = placements.find(p => p.key === placement.key);
              const percentage = maxValue > 0 ? ((placement.value - minValue) / (maxValue - minValue)) * 100 : 0;
              const isBest = selectedMetric === 'cpa' || selectedMetric === 'cpm' ? 
                placement.value === minValue : placement.value === maxValue;
              
              return (
                <div key={placement.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{placementInfo?.icon}</span>
                      <div>
                        <span className="text-sm font-medium text-slate-300">{placementInfo?.label}</span>
                        <div className="text-xs text-slate-500 capitalize">{placementInfo?.category}</div>
                      </div>
                      {isBest && (
                        <span className="px-2 py-1 text-xs bg-green-600/50 text-green-200 rounded-full">
                          Best
                        </span>
                      )}
                    </div>
                    <span className={`text-sm font-semibold ${getMetricColor(placement.value, selectedMetric, 'placement')}`}>
                      {formatValue(placement.value, metricData.format)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        isBest ? 'bg-green-500' : 'bg-purple-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Placement Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="text-2xl font-bold text-green-400">
              {placements.find(p => p.key === sortedPlacements[0]?.key)?.label}
            </div>
            <div className="text-sm text-slate-400">Top Placement</div>
            <div className="text-xs text-green-300">
              {formatValue(sortedPlacements[0]?.value || 0, metricData.format)}
            </div>
          </div>
          <div className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="text-2xl font-bold text-blue-400">
              {formatValue(
                sortedPlacements.reduce((sum, p) => sum + p.value, 0) / sortedPlacements.length,
                metricData.format
              )}
            </div>
            <div className="text-sm text-slate-400">Average</div>
          </div>
          <div className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="text-2xl font-bold text-red-400">
              {placements.find(p => p.key === sortedPlacements[sortedPlacements.length - 1]?.key)?.label}
            </div>
            <div className="text-sm text-slate-400">Bottom Placement</div>
            <div className="text-xs text-red-300">
              {formatValue(sortedPlacements[sortedPlacements.length - 1]?.value || 0, metricData.format)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderComparison = () => {
    const platformData = mockData.platform;
    const placementData = mockData.placement;
    const metricData = metrics.find(m => m.key === selectedMetric);
    
    if (!platformData || !placementData || !metricData) return <div>No data available</div>;

    // Find best performing platform and placement
    const bestPlatform = Object.entries(platformData)
      .sort((a, b) => {
        if (selectedMetric === 'cpa' || selectedMetric === 'cpm') {
          return a[1][selectedMetric] - b[1][selectedMetric];
        } else {
          return b[1][selectedMetric] - a[1][selectedMetric];
        }
      })[0];

    const bestPlacement = Object.entries(placementData)
      .sort((a, b) => {
        if (selectedMetric === 'cpa' || selectedMetric === 'cpm') {
          return a[1][selectedMetric] - b[1][selectedMetric];
        } else {
          return b[1][selectedMetric] - a[1][selectedMetric];
        }
      })[0];

    return (
      <div className="space-y-6">
        {/* Best Combination */}
        <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-6 border border-purple-500/30">
          <h5 className="text-lg font-semibold text-white mb-4">🏆 Best Performing Combination</h5>
          <div className="grid grid-cols-2 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">
                {platforms.find(p => p.key === bestPlatform[0])?.icon}
              </div>
              <div className="text-lg font-semibold text-white">
                {platforms.find(p => p.key === bestPlatform[0])?.label}
              </div>
              <div className="text-sm text-slate-400">
                {formatValue(bestPlatform[1][selectedMetric], metricData.format)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">
                {placements.find(p => p.key === bestPlacement[0])?.icon}
              </div>
              <div className="text-lg font-semibold text-white">
                {placements.find(p => p.key === bestPlacement[0])?.label}
              </div>
              <div className="text-sm text-slate-400">
                {formatValue(bestPlacement[1][selectedMetric], metricData.format)}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Matrix */}
        <div className="bg-slate-800/30 rounded-lg p-6">
          <h5 className="text-lg font-semibold text-white mb-4">Performance Matrix</h5>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-3 text-slate-300 font-medium">Platform</th>
                  {placements.map(placement => (
                    <th key={placement.key} className="text-center p-3 text-slate-300 font-medium">
                      <div className="flex flex-col items-center space-y-1">
                        <span className="text-lg">{placement.icon}</span>
                        <span className="text-xs">{placement.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {platforms.map(platform => (
                  <tr key={platform.key} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <td className="p-3 text-slate-300 font-medium">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{platform.icon}</span>
                        <span>{platform.label}</span>
                      </div>
                    </td>
                    {placements.map(placement => {
                      // Simulate platform-placement specific performance
                      const basePlatformValue = platformData[platform.key][selectedMetric];
                      const basePlacementValue = placementData[placement.key][selectedMetric];
                      const combinedValue = (basePlatformValue + basePlacementValue) / 2;
                      
                      return (
                        <td key={placement.key} className="text-center p-3">
                          <span className={`text-sm font-medium ${getMetricColor(combinedValue, selectedMetric, 'platform')}`}>
                            {formatValue(combinedValue, metricData.format)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Platform & Placement Analysis</h2>
        <p className="text-slate-400">Optimize for where each creative works best</p>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-4 gap-4">
        {/* Metric Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Performance Metric</h4>
          <div className="space-y-2">
            {metrics.map((metric) => (
              <button
                key={metric.key}
                onClick={() => setSelectedMetric(metric.key)}
                className={`w-full p-2 rounded-lg border transition-all ${
                  selectedMetric === metric.key
                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                    : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{metric.icon}</span>
                  <span className="text-sm font-medium">{metric.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Platform Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Platform Focus</h4>
          <div className="space-y-2">
            <button
              onClick={() => setSelectedPlatform('all')}
              className={`w-full p-2 rounded-lg border transition-all ${
                selectedPlatform === 'all'
                  ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                  : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
              }`}
            >
              All Platforms
            </button>
            {platforms.map((platform) => (
              <button
                key={platform.key}
                onClick={() => setSelectedPlatform(platform.key)}
                className={`w-full p-2 rounded-lg border transition-all ${
                  selectedPlatform === platform.key
                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                    : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{platform.icon}</span>
                  <span className="text-sm font-medium">{platform.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Placement Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Placement Focus</h4>
          <div className="space-y-2">
            <button
              onClick={() => setSelectedPlacement('all')}
              className={`w-full p-2 rounded-lg border transition-all ${
                selectedPlacement === 'all'
                  ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                  : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
              }`}
            >
              All Placements
            </button>
            {placements.map((placement) => (
              <button
                key={placement.key}
                onClick={() => setSelectedPlacement(placement.key)}
                className={`w-full p-2 rounded-lg border transition-all ${
                  selectedPlacement === placement.key
                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                    : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{placement.icon}</span>
                  <span className="text-sm font-medium">{placement.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* View Mode Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Analysis View</h4>
          <div className="space-y-2">
            <button
              onClick={() => setViewMode('platform')}
              className={`w-full p-2 rounded-lg border transition-all ${
                viewMode === 'platform'
                  ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                  : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4" />
                <span className="text-sm font-medium">Platform</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('placement')}
              className={`w-full p-2 rounded-lg border transition-all ${
                viewMode === 'placement'
                  ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                  : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Smartphone className="h-4 w-4" />
                <span className="text-sm font-medium">Placement</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('comparison')}
              className={`w-full p-2 rounded-lg border transition-all ${
                viewMode === 'comparison'
                  ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                  : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span className="text-sm font-medium">Comparison</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-slate-800/30 rounded-lg p-6">
        {viewMode === 'platform' && renderPlatformAnalysis()}
        {viewMode === 'placement' && renderPlacementAnalysis()}
        {viewMode === 'comparison' && renderComparison()}
      </div>

      {/* Insights */}
      <div className="bg-slate-800/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Key Insights</h4>
        <div className="space-y-3 text-sm text-slate-300">
          <div className="flex items-start space-x-2">
            <span className="text-green-400 mt-1">•</span>
            <span>
              <strong>TikTok</strong> shows the best overall performance for most metrics
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-blue-400 mt-1">•</span>
            <span>
              <strong>Reels/Shorts</strong> placements consistently outperform other video formats
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-purple-400 mt-1">•</span>
            <span>
              Consider allocating more budget to top-performing platform-placement combinations
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformPlacement;
